import { personalPersonasControllerCreate, personalPersonasControllerUpdate, personalPersonasControllerArchive } from "@/client/sdk.gen";
import type {
  PersonalPersonaInput,
  Persona,
  PruefungsAntwortRequest,
} from "@nl/shared";
import {
  type UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  aiControllerCoachThreadByIdOptions,
  aiControllerCoachThreadsOptions,
  aiControllerCoachThreadsQueryKey,
  aiControllerCreateCoachThreadMutation,
  aiControllerDeleteCoachThreadMutation,
  aiControllerUpdateCoachThreadMutation,
  aiControllerCoachThreadByIdQueryKey,
  analyticsControllerDimensionsOptions,
  analyticsControllerOverviewOptions,
  analyticsControllerTrainingsPerDayOptions,
  authControllerMeOptions,
  authControllerMeQueryKey,
  bereichFreigabeControllerEigeneOptions,
  bereichFreigabeControllerEigeneQueryKey,
  bereichFreigabeControllerListOptions,
  bereichFreigabeControllerListQueryKey,
  bereichFreigabeControllerSetMutation,
  einheitFreigabeControllerEigeneOptions,
  einheitFreigabeControllerEigeneQueryKey,
  einheitFreigabeControllerListOptions,
  einheitFreigabeControllerListQueryKey,
  einheitFreigabeControllerSetMutation,
  contentControllerCoachStylesOptions,
  contentControllerCoachStylesQueryKey,
  contentControllerPersonasOptions,
  contentControllerPersonasQueryKey,
  contentControllerRemoveCoachStyleMutation,
  contentControllerRemovePersonaMutation,
  contentControllerUpsertCoachStyleMutation,
  contentControllerUpsertPersonaMutation,
  learningEssenceControllerGetOptions,
  learningEssenceControllerGetQueryKey,
  learningEssenceControllerRemoveMutation,
  learningEssenceControllerSkipMutation,
  learningEssenceControllerStartMutation,
  pruefungControllerAntwortenMutation,
  pruefungControllerAuswertungenOptions,
  pruefungControllerAuswertungenQueryKey,
  pruefungControllerLernfelderOptions,
  pruefungControllerLernfelderQueryKey,
  pruefungControllerLernstandAbrufenOptions,
  pruefungControllerLernstandAbrufenQueryKey,
  pruefungControllerSetzeTerminMutation,
  pruefungControllerVerlaengereZielMutation,
  pruefungControllerZuruecksetzenMutation,
  processRunControllerAdvanceMutation,
  processRunControllerCreateMutation,
  processRunControllerDebriefOptions,
  processRunControllerGetOptions,
  processRunControllerGetQueryKey,
  processRunControllerListOptions,
  processRunControllerListQueryKey,
  processRunControllerAvToolMutation,
  processRunControllerLoesungMutation,
  processRunControllerNachbereitungMutation,
  processRunControllerPreparationMutation,
  reflectionControllerAdvanceMutation,
  reflectionControllerGetOptions,
  reflectionControllerGetQueryKey,
  reflectionControllerRemoveMutation,
  sessionsControllerCreateMutation,
  sessionsControllerGenerateFeedbackMutation,
  sessionsControllerGetOptions,
  sessionsControllerGetQueryKey,
  sessionsControllerListOptions,
  sessionsControllerListQueryKey,
  sessionsControllerRemoveMutation,
  teamsControllerListOptions,
  teamsControllerListQueryKey,
  tenantsControllerAuditLogOptions,
  tenantsControllerAuditLogPageInfiniteOptions,
  tenantsControllerAuditLogPageInfiniteQueryKey,
  tenantsControllerAuditLogQueryKey,
  tenantsControllerCreateMutation,
  tenantsControllerUpdateMutation,
  tenantsControllerGetOptions,
  tenantsControllerGetQueryKey,
  tenantsControllerListOptions,
  tenantsControllerListQueryKey,
  tenantsControllerSetEmailDomainsMutation,
  tenantsControllerSetBausteineMutation,
  tenantsControllerSubprozessorenOptions,
  usersControllerIssueOneTimePasswordMutation,
  usersControllerListOptions,
  usersControllerEntsperrenMutation,
  usersControllerListQueryKey,
  usersControllerRemoveMutation,
  usersControllerSperrenMutation,
  usersControllerUpsertMutation,
} from "@/client/@tanstack/react-query.gen";
import {
  invalidateGruppenFreigaben,
  invalidateKennzahlen,
} from "@/lib/team-queries";
import type {
  CoachStyleDto,
  CreateSessionDto,
  CreateTenantDto,
  FeedbackDto,
  OneTimePasswordDto,
  PersonaDto,
  ProcessRunDto,
  RateFeedbackDto,
  ReflectionSessionDto,
  StartProcessRunDto,
  SaveAvToolDto,
  SaveLoesungDto,
  SubmitNachbereitungDto,
  SubmitPreparationDto,
  TenantDto,
  TenantUserDto,
  TrainingSessionDto,
  UpdateEmailDomainsDto,
  UpdateTenantDto,
  UpdateTenantBausteineDto,
  UpsertCoachStyleDto,
  UpsertPersonaDto,
  UpsertUserDto,
} from "@/client/types.gen";

/**
 * Data access for the frontend.
 *
 * Types, fetch calls, query keys and query options all come from the generated
 * client in `src/client`, produced from the backend's OpenAPI spec. This file
 * only adds what cannot be generated: cache invalidation after mutations.
 */

// --- Inhalte ---

export function usePersonas() {
  return useQuery(contentControllerPersonasOptions());
}

export function useSavePersonalPersona() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id?: string; body: PersonalPersonaInput }): Promise<Persona> => {
      const result = id
        ? await personalPersonasControllerUpdate({ path: { id }, body, throwOnError: true })
        : await personalPersonasControllerCreate({ body, throwOnError: true });
      return result.data;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: contentControllerPersonasQueryKey() }),
  });
}

export function useArchivePersonalPersona() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => personalPersonasControllerArchive({ path: { id }, throwOnError: true }),
    onSuccess: () => client.invalidateQueries({ queryKey: contentControllerPersonasQueryKey() }),
  });
}

export function useUpsertPersona() {
  const client = useQueryClient();
  const base = useMutation({
    ...contentControllerUpsertPersonaMutation(),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: contentControllerPersonasQueryKey() }),
  });
  return flatBody<UpsertPersonaDto, PersonaDto>(base);
}

export function useRemovePersona() {
  const client = useQueryClient();
  const base = useMutation({
    ...contentControllerRemovePersonaMutation(),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: contentControllerPersonasQueryKey() }),
  });
  return flatId(base);
}

export function useCoachStyles() {
  return useQuery(contentControllerCoachStylesOptions());
}

export function useUpsertCoachStyle() {
  const client = useQueryClient();
  const base = useMutation({
    ...contentControllerUpsertCoachStyleMutation(),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: contentControllerCoachStylesQueryKey(),
      }),
  });
  return flatBody<UpsertCoachStyleDto, CoachStyleDto>(base);
}

export function useRemoveCoachStyle() {
  const client = useQueryClient();
  const base = useMutation({
    ...contentControllerRemoveCoachStyleMutation(),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: contentControllerCoachStylesQueryKey(),
      }),
  });
  return flatId(base);
}

// --- Training conversations ---

/** The advisor's conversations with their coach, newest first. */
export function useCoachThreads() {
  return useQuery(aiControllerCoachThreadsOptions());
}

export function useCoachThread(id: string | undefined) {
  return useQuery({
    ...aiControllerCoachThreadByIdOptions({ path: { id: id ?? "" } }),
    enabled: !!id,
    // The cache may hold the thread as it was during its first turn — fetched
    // while the coach was still streaming, so without a single message — and
    // nothing invalidates it afterwards. A conversation is seeded from this
    // data exactly once on mount, so the mount has to see the server's state.
    refetchOnMount: "always",
  });
}

/**
 * Reloads the rail. Needed after a finished turn: a thread gets its title from
 * the first answer, so at creation time there is nothing to list yet.
 */
export function useRefreshCoachThreads() {
  const client = useQueryClient();
  return () =>
    client.invalidateQueries({ queryKey: aiControllerCoachThreadsQueryKey() });
}

export function useCreateCoachThread() {
  const client = useQueryClient();
  return useMutation({
    ...aiControllerCreateCoachThreadMutation(),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: aiControllerCoachThreadsQueryKey() }),
  });
}

/** Title and favorite flag share one PATCH; both list and detail views refresh. */
export function useUpdateCoachThread() {
  const client = useQueryClient();
  return useMutation({
    ...aiControllerUpdateCoachThreadMutation(),
    onSuccess: (_, variables) => Promise.all([
      client.invalidateQueries({ queryKey: aiControllerCoachThreadsQueryKey() }),
      client.invalidateQueries({ queryKey: aiControllerCoachThreadByIdQueryKey({ path: variables.path }) }),
    ]),
  });
}

export function useDeleteCoachThread() {
  const client = useQueryClient();
  return useMutation({
    ...aiControllerDeleteCoachThreadMutation(),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: aiControllerCoachThreadsQueryKey() }),
  });
}

export function useSessions() {
  return useQuery(sessionsControllerListOptions());
}

export function useSession(
  id: string,
  options?: Partial<UseQueryOptions<TrainingSessionDto>>,
) {
  return useQuery({
    ...sessionsControllerGetOptions({ path: { id } }),
    ...options,
  } as UseQueryOptions<TrainingSessionDto>);
}

/**
 * Das Reflexionsgespräch zu einer Phase.
 *
 * Eigene Abfrage statt eines Felds an der Sitzung, weil die Selbsteinschätzung
 * nur hier mitreist und nicht in jeder Sitzungsliste liegt.
 */
export function useReflection(sessionId: string) {
  return useQuery({
    ...reflectionControllerGetOptions({ path: { id: sessionId } }),
  } as UseQueryOptions<ReflectionSessionDto>);
}

/**
 * Schaltet die Stufe weiter.
 *
 * Fast immer ist der Auslöser der Coach über sein Werkzeug. Von hier kommt der
 * Fall, in dem die Beraterin die Einschätzung ausdrücklich anfordert — dann
 * übergeht der Server seine Freigabebedingungen.
 */
export function useAdvanceReflection(sessionId: string) {
  const client = useQueryClient();
  const base = useMutation({
    ...reflectionControllerAdvanceMutation(),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: reflectionControllerGetQueryKey({ path: { id: sessionId } }),
      });
      void client.invalidateQueries({
        queryKey: sessionsControllerGetQueryKey({ path: { id: sessionId } }),
      });
    },
  });
  return base;
}

/** Löscht die Selbsteinschätzung und behält die Bilanz. */
export function useDeleteSelfAssessment(sessionId: string) {
  const client = useQueryClient();
  return useMutation({
    ...reflectionControllerRemoveMutation(),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: reflectionControllerGetQueryKey({ path: { id: sessionId } }),
      }),
  });
}

// --- Lernentwicklungs-Essenz ---

export function useLearningEssence() {
  return useQuery(learningEssenceControllerGetOptions());
}

function useInvalidateLearningEssence() {
  const client = useQueryClient();
  return () =>
    client.invalidateQueries({ queryKey: learningEssenceControllerGetQueryKey() });
}

export function useStartLearningEssence() {
  const invalidate = useInvalidateLearningEssence();
  return useMutation({
    ...learningEssenceControllerStartMutation(),
    onSuccess: () => void invalidate(),
  });
}

export function useSkipLearningEssence() {
  const invalidate = useInvalidateLearningEssence();
  return useMutation({
    ...learningEssenceControllerSkipMutation(),
    onSuccess: () => void invalidate(),
  });
}

export function useDeleteLearningEssence() {
  const invalidate = useInvalidateLearningEssence();
  return useMutation({
    ...learningEssenceControllerRemoveMutation(),
    onSuccess: () => void invalidate(),
  });
}

// --- Prüfungsvorbereitung ---

export function usePruefungsLernfelder() {
  return useQuery(pruefungControllerLernfelderOptions());
}

export function usePruefungsLernstand() {
  return useQuery(pruefungControllerLernstandAbrufenOptions());
}

/**
 * Die Auswertungen eines Gesprächs, einmal geladen.
 *
 * Damit beantwortete Karten nach dem Neuladen im Ergebniszustand stehen statt
 * wieder offen. Bewusst eine Abfrage für den ganzen Verlauf statt einer je
 * Karte: ein Gespräch kann zwanzig Fragen enthalten.
 */
export function usePruefungsAuswertungen(threadId: string | undefined) {
  return useQuery({
    ...pruefungControllerAuswertungenOptions({ path: { id: threadId ?? "" } }),
    enabled: !!threadId,
  });
}

function useInvalidatePruefung() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({
      queryKey: pruefungControllerLernfelderQueryKey(),
    });
    void client.invalidateQueries({
      queryKey: pruefungControllerLernstandAbrufenQueryKey(),
    });
  };
}

export function useAntwortenPruefungsfrage(threadId: string | undefined) {
  const client = useQueryClient();
  const invalidate = useInvalidatePruefung();
  const base = useMutation({
    ...pruefungControllerAntwortenMutation(),
    onSuccess: () => {
      invalidate();
      if (threadId) {
        void client.invalidateQueries({
          queryKey: pruefungControllerAuswertungenQueryKey({
            path: { id: threadId },
          }),
        });
      }
    },
  });
  return flatBody<
    PruefungsAntwortRequest,
    { antwortId: string; richtig: boolean }
  >(base);
}

export function useSetzePruefungstermin() {
  const invalidate = useInvalidatePruefung();
  const base = useMutation({
    ...pruefungControllerSetzeTerminMutation(),
    onSuccess: () => invalidate(),
  });
  return flatBody<{ pruefungAm?: string | null }, unknown>(base);
}

export function useVerlaengerePruefungsziel(threadId: string | undefined) {
  const base = useMutation(pruefungControllerVerlaengereZielMutation());
  return flatBody<{ anzahl: number }, { ziel: number }>(base, {
    id: threadId ?? "",
  });
}

export function useResetPruefungsLernstand() {
  const invalidate = useInvalidatePruefung();
  return useMutation({
    ...pruefungControllerZuruecksetzenMutation(),
    onSuccess: () => invalidate(),
  });
}

export function useCreateSession() {
  const client = useQueryClient();
  const base = useMutation({
    ...sessionsControllerCreateMutation(),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: sessionsControllerListQueryKey() }),
  });
  return flatBody<CreateSessionDto, TrainingSessionDto>(base);
}

export function useDeleteSession() {
  const client = useQueryClient();
  const base = useMutation({
    ...sessionsControllerRemoveMutation(),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: sessionsControllerListQueryKey() }),
  });
  return flatId(base);
}

export function useGenerateFeedback() {
  const client = useQueryClient();
  const base = useMutation({
    ...sessionsControllerGenerateFeedbackMutation(),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({
        queryKey: sessionsControllerGetQueryKey({ path: variables.path }),
      });
      void client.invalidateQueries({ queryKey: sessionsControllerListQueryKey() });
    },
  });
  return flatId<FeedbackDto>(base);
}

// --- Prozess-Durchläufe (Beraterreise) ---

export function useProcessRuns() {
  return useQuery(processRunControllerListOptions());
}

export function useProcessRun(
  id: string,
  options?: Partial<UseQueryOptions<ProcessRunDto>>,
) {
  return useQuery({
    ...processRunControllerGetOptions({ path: { id } }),
    ...options,
  } as UseQueryOptions<ProcessRunDto>);
}

export function useProcessRunDebrief(id: string) {
  return useQuery(processRunControllerDebriefOptions({ path: { id } }));
}

export function useCreateProcessRun() {
  const client = useQueryClient();
  const base = useMutation({
    ...processRunControllerCreateMutation(),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: processRunControllerListQueryKey() }),
  });
  return flatBody<StartProcessRunDto, ProcessRunDto>(base);
}

export function useAdvanceStage(runId: string) {
  const client = useQueryClient();
  const base = useMutation({
    ...processRunControllerAdvanceMutation(),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: processRunControllerGetQueryKey({ path: { id: runId } }),
      }),
  });
  return flatId<ProcessRunDto>(base);
}

export function useSubmitPreparation(runId: string) {
  const client = useQueryClient();
  const base = useMutation({
    ...processRunControllerPreparationMutation(),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: processRunControllerGetQueryKey({ path: { id: runId } }),
      }),
  });
  return flatBody<SubmitPreparationDto, ProcessRunDto>(base, { id: runId });
}

/**
 * Submits the follow-up sheet and thereby releases the overall picture.
 *
 * Unlike the preparation this does not end the unit — the run stays where it
 * is, only the step changes. Invalidating the run is therefore enough: the
 * switch reads `caseState.nachbereitung`.
 */
export function useSubmitNachbereitung(runId: string) {
  const client = useQueryClient();
  const base = useMutation({
    ...processRunControllerNachbereitungMutation(),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: processRunControllerGetQueryKey({ path: { id: runId } }),
      }),
  });
  return flatBody<SubmitNachbereitungDto, ProcessRunDto>(base, { id: runId });
}

/**
 * Speichert den Stand des AV-Tools.
 *
 * Wird entprellt aus dem Werkzeugfenster aufgerufen, also oft. Die
 * Invalidierung des Durchlaufs ist trotzdem richtig: der Server rechnet das
 * Ergebnis neu, und das Arbeitsblatt daneben liest aus derselben Antwort.
 */
export function useSaveAvTool(runId: string) {
  const client = useQueryClient();
  const base = useMutation({
    ...processRunControllerAvToolMutation(),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: processRunControllerGetQueryKey({ path: { id: runId } }),
      }),
  });
  return flatBody<SaveAvToolDto, ProcessRunDto>(base, { id: runId });
}

/** Stores the product selection of "Lösung & Abschluss", debounced like the AV tool. */
export function useSaveLoesung(runId: string) {
  const client = useQueryClient();
  const base = useMutation({
    ...processRunControllerLoesungMutation(),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: processRunControllerGetQueryKey({ path: { id: runId } }),
      }),
  });
  return flatBody<SaveLoesungDto, ProcessRunDto>(base, { id: runId });
}

// --- Administration ---

/**
 * Nutzer des Mandanten. Ohne Argument der Mandant der Sitzung; mit `tenantId`
 * gezielt eine Sparkasse, was nur die Plattformrolle darf und die
 * Sparkassen-Detailansicht braucht.
 */
export function useTenantUsers(tenantId?: string) {
  return useQuery(
    usersControllerListOptions(tenantId ? { query: { tenantId } } : {}),
  );
}

export function useUpsertUser() {
  const client = useQueryClient();
  const base = useMutation({
    ...usersControllerUpsertMutation(),
    onSuccess: () => {
      // The editor can change the group, which moves member counts and the
      // aggregates. Only the table is awaited: the editor closes on fresh rows.
      void client.invalidateQueries({ queryKey: teamsControllerListQueryKey() });
      void client.invalidateQueries({ queryKey: tenantsControllerListQueryKey() });
      void client.invalidateQueries({ queryKey: tenantsControllerAuditLogQueryKey() });
      void client.invalidateQueries({
        queryKey: tenantsControllerAuditLogPageInfiniteQueryKey(),
      });
      invalidateKennzahlen(client);
      return client.invalidateQueries({ queryKey: usersControllerListQueryKey() });
    },
  });
  return flatBody<UpsertUserDto, TenantUserDto>(base);
}

/**
 * Vergibt ein Einmal-Passwort. Rückfallebene, wenn der Anmeldelink im
 * Mailfilter hängen bleibt; das Passwort kommt genau einmal zurück.
 */
export function useIssueOneTimePassword(tenantId?: string) {
  const client = useQueryClient();
  const base = useMutation({
    ...usersControllerIssueOneTimePasswordMutation(),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: usersControllerListQueryKey() }),
  });
  return {
    isPending: base.isPending,
    mutate: (
      userId: string,
      variant: OneTimePasswordDto["variant"],
      options?: {
        onSuccess?: (data: OneTimePasswordDto) => void;
        onError?: (error: unknown) => void;
      },
    ) =>
      base.mutate(
        {
          path: { id: userId },
          body: { variant },
          ...(tenantId ? { query: { tenantId } } : {}),
        },
        options,
      ),
  };
}

export function useTeams(tenantId?: string) {
  return useQuery(
    teamsControllerListOptions(tenantId ? { query: { tenantId } } : {}),
  );
}

export function useTenants(options?: { enabled?: boolean }) {
  return useQuery({ ...tenantsControllerListOptions(), ...options });
}

export function useTenant(id: string) {
  return useQuery(tenantsControllerGetOptions({ path: { id } }));
}

/**
 * Setzt die E-Mail-Domains, über die sich Berater:innen der Sparkasse selbst
 * anmelden dürfen. Nur die Plattformrolle.
 */
export function useSetEmailDomains(tenantId: string) {
  const client = useQueryClient();
  const base = useMutation({
    ...tenantsControllerSetEmailDomainsMutation(),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: tenantsControllerGetQueryKey({ path: { id: tenantId } }),
      });
      void client.invalidateQueries({ queryKey: tenantsControllerListQueryKey() });
      void client.invalidateQueries({ queryKey: tenantsControllerAuditLogQueryKey() });
    },
  });
  return flatBody<UpdateEmailDomainsDto, TenantDto>(base, { id: tenantId });
}

/** What a Sparkasse trains, e.g. the income report with disability cover. Platform only. */
export function useSetTenantBausteine(tenantId: string) {
  const client = useQueryClient();
  const base = useMutation({
    ...tenantsControllerSetBausteineMutation(),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: tenantsControllerGetQueryKey({ path: { id: tenantId } }),
      });
      void client.invalidateQueries({ queryKey: tenantsControllerAuditLogQueryKey() });
    },
  });
  return flatBody<UpdateTenantBausteineDto, TenantDto>(base, { id: tenantId });
}

// --- Freischaltung von Trainingseinheiten ---

/** The caller's own Sparkasse, for the advisor's configurator. */
export function useEinheitFreigaben() {
  return useQuery(einheitFreigabeControllerEigeneOptions());
}

export function useTenantEinheitFreigaben(tenantId: string) {
  return useQuery(
    einheitFreigabeControllerListOptions({ path: { id: tenantId } }),
  );
}

export function useSetEinheitFreigabe(tenantId: string) {
  const client = useQueryClient();
  return useMutation({
    ...einheitFreigabeControllerSetMutation(),
    onSuccess: (data) => {
      client.setQueryData(
        einheitFreigabeControllerListQueryKey({ path: { id: tenantId } }),
        data,
      );
      // The platform role may be switched into this very tenant.
      void client.invalidateQueries({
        queryKey: einheitFreigabeControllerEigeneQueryKey(),
      });
      void client.invalidateQueries({ queryKey: tenantsControllerAuditLogQueryKey() });
      invalidateGruppenFreigaben(client);
    },
  });
}

/** The caller's own Sparkasse, for navigation and the area gates. */
export function useBereichFreigaben() {
  return useQuery(bereichFreigabeControllerEigeneOptions());
}

export function useTenantBereichFreigaben(tenantId: string) {
  return useQuery(
    bereichFreigabeControllerListOptions({ path: { id: tenantId } }),
  );
}

export function useSetBereichFreigabe(tenantId: string) {
  const client = useQueryClient();
  return useMutation({
    ...bereichFreigabeControllerSetMutation(),
    onSuccess: (data) => {
      client.setQueryData(
        bereichFreigabeControllerListQueryKey({ path: { id: tenantId } }),
        data,
      );
      // The platform role may be switched into this very tenant.
      void client.invalidateQueries({
        queryKey: bereichFreigabeControllerEigeneQueryKey(),
      });
      void client.invalidateQueries({ queryKey: tenantsControllerAuditLogQueryKey() });
      invalidateGruppenFreigaben(client);
    },
  });
}

export function useAuditLog(tenantId?: string) {
  return useQuery(
    tenantsControllerAuditLogOptions({ query: tenantId ? { tenantId } : {} }),
  );
}

export function useSubprozessoren() {
  return useQuery({
    ...tenantsControllerSubprozessorenOptions(),
    staleTime: 10 * 60_000,
  });
}

// --- Verwaltung ---

function useInvalidateVerwaltung() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: tenantsControllerListQueryKey() });
    void client.invalidateQueries({ queryKey: teamsControllerListQueryKey() });
    void client.invalidateQueries({ queryKey: usersControllerListQueryKey() });
    void client.invalidateQueries({ queryKey: tenantsControllerAuditLogQueryKey() });
    void client.invalidateQueries({
      queryKey: tenantsControllerAuditLogPageInfiniteQueryKey(),
    });
  };
}

export function useCreateTenant() {
  const invalidate = useInvalidateVerwaltung();
  const base = useMutation({
    ...tenantsControllerCreateMutation(),
    onSuccess: invalidate,
  });
  return flatBody<CreateTenantDto, TenantDto>(base);
}

export function useUpdateTenant(tenantId: string) {
  const client = useQueryClient();
  const invalidate = useInvalidateVerwaltung();
  const base = useMutation({
    ...tenantsControllerUpdateMutation(),
    onSuccess: () => {
      invalidate();
      void client.invalidateQueries({
        queryKey: tenantsControllerGetQueryKey({ path: { id: tenantId } }),
      });
    },
  });
  return flatBody<UpdateTenantDto, TenantDto>(base, { id: tenantId });
}

/** Locks or unlocks an account; locking revokes its sessions at once. */
export function useSetUserGesperrt(tenantId?: string) {
  const invalidate = useInvalidateVerwaltung();
  const sperren = useMutation({
    ...usersControllerSperrenMutation(),
    onSuccess: invalidate,
  });
  const entsperren = useMutation({
    ...usersControllerEntsperrenMutation(),
    onSuccess: invalidate,
  });
  return {
    isPending: sperren.isPending || entsperren.isPending,
    mutate: (
      userId: string,
      gesperrt: boolean,
      options?: {
        onSuccess?: (data: TenantUserDto) => void;
        onError?: (error: unknown) => void;
      },
    ) =>
      (gesperrt ? sperren : entsperren).mutate(
        {
          path: { id: userId },
          ...(tenantId ? { query: { tenantId } } : {}),
        },
        options,
      ),
  };
}

/** Deletes a locked account for good, including its training data. */
export function useDeleteUser(tenantId?: string) {
  const invalidate = useInvalidateVerwaltung();
  const base = useMutation({
    ...usersControllerRemoveMutation(),
    onSuccess: invalidate,
  });
  return {
    isPending: base.isPending,
    mutate: (
      userId: string,
      options?: {
        onSuccess?: () => void;
        onError?: (error: unknown) => void;
      },
    ) =>
      base.mutate(
        {
          path: { id: userId },
          ...(tenantId ? { query: { tenantId } } : {}),
        },
        options,
      ),
  };
}

export type AuditLogFilter = {
  tenantId?: string;
  action?: string;
  von?: string;
  bis?: string;
};

export function useAuditLogPages(filter: AuditLogFilter) {
  const query = Object.fromEntries(
    Object.entries(filter).filter(([, value]) => Boolean(value)),
  ) as AuditLogFilter;
  return useInfiniteQuery({
    ...tenantsControllerAuditLogPageInfiniteOptions({ query }),
    // An empty cursor is the first page; the server treats it as absent.
    initialPageParam: "",
    getNextPageParam: (last) => last.nextCursor,
  });
}

// --- Auswertung ---

export function useAnalyticsOverview(days = 30) {
  return useQuery(
    analyticsControllerOverviewOptions({ query: { days: String(days) } }),
  );
}

export function useTrainingsPerDay(days = 30) {
  return useQuery(
    analyticsControllerTrainingsPerDayOptions({
      query: { days: String(days) },
    }),
  );
}

export function useDimensionAverages(days = 30) {
  return useQuery(
    analyticsControllerDimensionsOptions({ query: { days: String(days) } }),
  );
}

// --- Sitzung ---

export function useMe(enabled = true) {
  return useQuery({
    ...authControllerMeOptions(),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

export const queryKeys = {
  me: () => authControllerMeQueryKey(),
  sessions: () => sessionsControllerListQueryKey(),
  session: (id: string) => sessionsControllerGetQueryKey({ path: { id } }),
};

export type {
  CoachStyleDto,
  CreateSessionDto,
  FeedbackDto,
  OneTimePasswordDto,
  PersonaDto,
  RateFeedbackDto,
  TenantDto,
  TenantUserDto,
  TrainingSessionDto,
  UpdateEmailDomainsDto,
  UpsertCoachStyleDto,
  UpsertPersonaDto,
  UpsertUserDto,
};

/**
 * Adapter between the generated client's call shape ({ body, path }) and the
 * flat shape the interface uses.
 *
 * Without this layer every call site would have to know the transport shape.
 */
type FlatMutation<TInput, TResult> = {
  mutate: (
    input: TInput,
    options?: {
      onSuccess?: (data: TResult) => void;
      onError?: (error: unknown) => void;
    },
  ) => void;
  mutateAsync: (input: TInput) => Promise<TResult>;
  isPending: boolean;
};

function flatBody<TInput, TResult>(
  base: any,
  path?: Record<string, string>,
): FlatMutation<TInput, TResult> {
  return {
    isPending: base.isPending,
    mutate: (input, options) =>
      base.mutate({ body: input, ...(path ? { path } : {}) }, options),
    mutateAsync: (input) =>
      base.mutateAsync({ body: input, ...(path ? { path } : {}) }),
  };
}

function flatId<TResult = unknown>(base: any): FlatMutation<string, TResult> {
  return {
    isPending: base.isPending,
    mutate: (id, options) => base.mutate({ path: { id } }, options),
    mutateAsync: (id) => base.mutateAsync({ path: { id } }),
  };
}
